import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const GOLD = '#C9A84C';

const helpSections = [
  {
    titleKey: 'help.getting_started',
    items: [
      { labelKey: 'help.home_label', descKey: 'help.home_desc' },
      { labelKey: 'help.settings_label', descKey: 'help.settings_desc' },
      { labelKey: 'help.search_label', descKey: 'help.search_desc' },
    ],
  },
  {
    titleKey: 'help.playing_music',
    items: [
      { labelKey: 'help.play_track_label', descKey: 'help.play_track_desc' },
      { labelKey: 'help.mini_player_label', descKey: 'help.mini_player_desc' },
      { labelKey: 'help.queue_label', descKey: 'help.queue_desc' },
    ],
  },
  {
    titleKey: 'help.streaming_services',
    items: [
      { labelKey: 'help.qobuz_label', descKey: 'help.qobuz_desc' },
      { labelKey: 'help.live_radio_label', descKey: 'help.live_radio_desc' },
      { labelKey: 'help.octave_radio_label', descKey: 'help.octave_radio_desc' },
    ],
  },
  {
    titleKey: 'help.your_valet',
    items: [
      { labelKey: 'help.picks_label', descKey: 'help.picks_desc' },
      { labelKey: 'help.chat_label', descKey: 'help.chat_desc' },
      { labelKey: 'help.trending_label', descKey: 'help.trending_desc' },
    ],
  },
  {
    titleKey: 'help.local_features',
    items: [
      { labelKey: 'help.local_lib_label', descKey: 'help.local_lib_desc' },
      { labelKey: 'help.renderers_label', descKey: 'help.renderers_desc' },
    ],
  },
  {
    titleKey: 'help.playlists_favorites',
    items: [
      { labelKey: 'help.favorites_label', descKey: 'help.favorites_desc' },
      { labelKey: 'help.playlists_label', descKey: 'help.playlists_desc' },
      { labelKey: 'help.search_pl_label', descKey: 'help.search_pl_desc' },
    ],
  },
];

export default function HelpSlideout({ open, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!shouldRender) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 998,
          background: `rgba(0,0,0,${visible ? 0.5 : 0})`,
          backdropFilter: visible ? 'blur(4px)' : 'none',
          WebkitBackdropFilter: visible ? 'blur(4px)' : 'none',
          transition: 'background 0.3s ease, backdrop-filter 0.3s ease',
        }}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 420, zIndex: 999,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border-light)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '20px 20px 16px', paddingTop: 'max(20px, env(safe-area-inset-top, 20px))', borderBottom: '1px solid var(--border-light)',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: 'var(--bg-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-secondary)', cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            title={t('help.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
          </button>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{t('help.title')}</h1>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 32px' }} className="no-scrollbar">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, padding: '16px 0', borderBottom: '1px solid var(--border-light)' }}>
            {t('help.intro')}
          </p>

          {/* Guided Tour CTA */}
          <button
            onClick={() => {
              onClose();
              setTimeout(() => window.dispatchEvent(new CustomEvent('maestro:start-tour')), 400);
            }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 18px', margin: '16px 0', borderRadius: 16,
              background: `linear-gradient(135deg, rgba(201,168,76,0.1), rgba(201,168,76,0.04))`,
              border: '1px solid rgba(201,168,76,0.2)',
              cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)'; e.currentTarget.style.background = 'rgba(201,168,76,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.2)'; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201,168,76,0.1), rgba(201,168,76,0.04))'; }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              background: 'rgba(201,168,76,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill={GOLD} stroke="none" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Take the Guided Tour</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.7, lineHeight: 1.4 }}>
                A narrated walkthrough of every feature — with voice and visual highlights
              </div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          {helpSections.map((section) => (
            <div key={section.titleKey}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: GOLD,
                padding: '20px 0 10px',
              }}>
                {t(section.titleKey)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {section.items.map((item) => (
                  <div key={item.labelKey} style={{
                    padding: '12px 14px', borderRadius: 12,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {t(item.labelKey)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                      {t(item.descKey)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{
            marginTop: 24, padding: 16, borderRadius: 14,
            background: `rgba(201,168,76,0.06)`,
            border: `1px solid rgba(201,168,76,0.15)`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {t('help.footer')}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
